/*
 * The task tray's data (docs/ux-principles.md; Ryan, 2026-10-21: "any running processes should pop up … with a
 * progress bar so the user can continue to navigate the app"). Everything comes from the API's operations list for
 * the org, kept live by the event stream and polled while something runs, so the tray survives navigation and
 * reloads and agrees with every in-page progress view (they read the same operations). The person's own work in
 * other orgs (api/work.ts) comes the same way, with its org.
 *
 * A task is one piece of work a person would name: a deploy, an update run, a copy, a backup, a restore, a restart.
 * A restore point and the work it protects are one task in two steps. Background reads (health checks, inventories,
 * pipeline lookups, re-applied configuration) aren't tasks.
 *
 * Progress: by steps, and within a step by how long the same kind of work usually takes on that server (the median
 * of its recent successful runs). Without that history the step is indeterminate, and once a step runs well past
 * its usual time it turns indeterminate too, rather than sitting at 99%.
 */
import { useQueries } from "@tanstack/react-query";
import { api } from "./client";
import { keys } from "./keys";
import { isActiveOp, type Operation, type OperationOutcome } from "./types";
import type { WorkOp, WorkOrg, WorkPlace } from "./work";

/** The kinds a person starts and waits on. */
export const TASK_KINDS = new Set([
  "site.deploy",
  "site.rollback",
  "site.update",
  "site.remediate",
  "site.scan",
  "site.remove",
  "env.clone",
  "backup.create",
  "backup.restore",
  "backup.verify",
  "backup.export",
  "mail.backup",
  "mail.restore",
  "mail.domain.remove",
  "server.reboot",
  "server.upgrade",
  "server.php.upgrade",
  "server.php.install",
  "server.refresh",
  "server.bootstrap",
  "agent.update",
  // A Node.js app's restart and "Run now" (pages/sites/node/).
  "site.app.restart",
  "site.app.job.run",
]);

/** Kinds whose start gets the "Started · See progress" toast (the rest already say so where they start). */
export const START_TOAST_KINDS = new Set(["site.deploy", "site.rollback", "site.update", "env.clone", "backup.create", "backup.restore", "server.reboot", "server.upgrade", "server.refresh"]);

/** How long finished work stays in "Done recently". */
export const RECENT_MS = 10 * 60_000;

export type StepState = "done" | "running" | "starting" | "waiting" | "failed" | "skipped";
export type TaskStep = { label: string; op: Operation | null; state: StepState; estimateMs: number | null };
export type TaskStatus = "running" | "succeeded" | "failed" | "cancelled";
export type Task = {
  /** The first operation's id (the restore point, for a chain). */
  id: string;
  /** The work's kind ("site.deploy"; for a chain, the work after the restore point), for the header's words. */
  kind: string;
  /** What it is: the work's own words ("Update WP Mail SMTP on Live"). */
  title: string;
  siteId: string | null;
  environmentId: string | null;
  serverId: string;
  steps: TaskStep[];
  status: TaskStatus;
  startedAt: string;
  finishedAt: string | null;
  /** The operation whose log to open: the current step's, else the last that ran. */
  logOp: string;
  error: string | null;
  /** What came of it, in the API's words ("Installed 6 security updates" / "No restart needed."), once it's done. */
  outcome: OperationOutcome | null;
  actor: Operation["actor"];
  /** Work in another org (the person's own, api/work.ts): that org, and the names its "where" line reads. Null here. */
  org: WorkOrg | null;
  place: WorkPlace | null;
};

/** The operation kind a restore point's work will be, before it starts (its `followUp.type`). */
const INTENT_KIND: Record<string, string> = { deploy: "site.deploy", clone: "env.clone", restore: "backup.restore", update: "site.update", remediate: "site.remediate", remove_environment: "site.remove", remove_site: "site.remove", create_site: "site.apply", create_environment: "site.apply", apply: "site.apply", php_settings: "site.apply", address_rewrite: "site.wp" };

/** How the header names running work of one kind ("2 updates running"); a kind without a name is a "task". */
const NOUNS: Record<string, readonly [string, string]> = {
  "site.deploy": ["deploy", "deploys"],
  "site.rollback": ["rollback", "rollbacks"],
  "site.update": ["update", "updates"],
  "server.upgrade": ["update", "updates"],
  "server.php.upgrade": ["update", "updates"],
  "server.php.install": ["install", "installs"],
  "agent.update": ["update", "updates"],
  "site.remediate": ["fix", "fixes"],
  "site.scan": ["scan", "scans"],
  "env.clone": ["copy", "copies"],
  "backup.create": ["backup", "backups"],
  "mail.backup": ["backup", "backups"],
  "backup.restore": ["restore", "restores"],
  "mail.restore": ["restore", "restores"],
  "backup.export": ["export", "exports"],
  "server.reboot": ["restart", "restarts"],
  "server.refresh": ["restart", "restarts"],
  // Bulk runs, by action.
  "updates.selected": ["update", "updates"],
  "updates.install": ["update", "updates"],
  "health.check": ["check", "checks"],
  // A server move (api/moves.ts), one task for the whole move.
  "env.move": ["move", "moves"],
};

/** "1 update running", "2 backups running"; work of more than one kind: "3 tasks running". `kind`: an operation kind or a bulk run's action. */
export function runningWords(work: { kind: string; count: number }[]): string {
  const TASK = ["task", "tasks"] as const;
  const n = work.reduce((sum, w) => sum + w.count, 0);
  const names = new Set(work.map((w) => (NOUNS[w.kind] ?? TASK)[0]));
  const [one, many] = names.size === 1 && work[0] ? (NOUNS[work[0].kind] ?? TASK) : TASK;
  return `${n} ${n === 1 ? one : many}`;
}

const ms = (a: string | null | undefined, b: string | null | undefined) => (a && b ? Date.parse(b) - Date.parse(a) : null);

/** Typical durations on one server, by kind: the median of up to ten recent successful runs (two at least). */
export type Typical = Map<string, number>;
export function typicalFrom(ops: Operation[]): Typical {
  const by = new Map<string, number[]>();
  for (const o of ops) {
    if (o.status !== "succeeded") continue;
    const d = ms(o.startedAt ?? o.createdAt, o.finishedAt);
    if (d === null || d < 0) continue;
    // A restore point is a backup, timed with the others of its kind.
    const list = by.get(o.kind) ?? [];
    if (list.length < 10) list.push(d);
    by.set(o.kind, list);
  }
  const out: Typical = new Map();
  for (const [k, v] of by) {
    if (v.length < 2) continue;
    const s = [...v].sort((a, b) => a - b);
    const mid = Math.floor(s.length / 2);
    out.set(k, s.length % 2 ? s[mid]! : (s[mid - 1]! + s[mid]!) / 2);
  }
  return out;
}

const opState = (o: Operation | null): StepState => {
  if (!o) return "waiting";
  if (o.status === "succeeded") return "done";
  if (o.status === "queued" || o.status === "sent") return "starting";
  if (isActiveOp(o.status)) return "running";
  return "failed";
};

/**
 * A restore point (an operation with `followUp`) and the work it dispatched, as two steps. The first is a restore
 * point, or a PHP version installed on the server first. The work is "starting" from the moment the first is done
 * until its own operation says otherwise.
 */
/** Work that ran without an operation of its own once the restore point was ready (a database change, an empty site's delete). */
const done = (f: NonNullable<Operation["followUp"]>) => f.status === "dispatched" && !f.operationId;

function chainSteps(o: Operation, work: Operation | null, est: (kind: string, op?: Operation | null) => number | null): [TaskStep, TaskStep] {
  const f = o.followUp!;
  const first = o.kind === "server.php.install";
  const rp: TaskStep = { label: first ? o.title : "Restore point", op: o, state: opState(o), estimateMs: est(first ? o.kind : "backup.create", o) };
  const next: TaskStep = {
    label: f.label,
    op: work,
    state: f.status === "cancelled" ? "skipped" : work ? opState(work) : done(f) ? "done" : rp.state === "done" ? "starting" : "waiting",
    // Before the work starts, what work of its kind usually takes here, so both steps weigh their time from the start.
    estimateMs: work ? est(work.kind, work) : INTENT_KIND[f.type ?? ""] ? est(INTENT_KIND[f.type ?? ""]!, null) : null,
  };
  return [rp, next];
}

/**
 * The org's operations as tasks: running ones, and those that finished within the last ten minutes. A restore point
 * and the work it dispatched are one task; that work isn't listed again on its own. `skip`: operations shown
 * elsewhere (a bulk run's), left out as tasks but still read, so a chain always finds its work.
 */
export function tasksFrom(ops: (Operation | WorkOp)[], typical: (serverId: string) => Typical | undefined, now = Date.now(), skip?: Set<string>): { running: Task[]; recent: Task[] } {
  const byId = new Map(ops.map((o) => [o.id, o]));
  const away = (o: Operation | WorkOp) => ("org" in o ? { org: o.org, place: o.where } : { org: null, place: null });
  const followUps = new Set(ops.map((o) => o.followUp?.operationId).filter((x): x is string => !!x));
  const tasks: Task[] = [];
  for (const o of ops) {
    // A server move's steps are one task of their own (app/TaskTray.tsx, MoveRow).
    if (o.quiet || followUps.has(o.id) || o.moveId || skip?.has(o.id)) continue;
    const t = typical(o.serverId);
    // The API's own estimate on a running operation first; else what the server's recent ones of that kind took.
    const est = (kind: string, op?: Operation | null) => (op?.estimateSeconds ? op.estimateSeconds * 1000 : (t?.get(kind) ?? null));
    if (o.followUp) {
      const f = o.followUp;
      const work = f.operationId ? (byId.get(f.operationId) ?? null) : null;
      const [rp, next] = chainSteps(o, work, est);
      const status: TaskStatus =
        f.status === "cancelled" ? "failed" : done(f) ? (isActiveOp(o.status) ? "running" : o.status === "succeeded" ? "succeeded" : "failed") : work && !isActiveOp(work.status) ? (work.status === "succeeded" ? "succeeded" : work.status === "cancelled" ? "cancelled" : "failed") : "running";
      tasks.push({
        id: o.id,
        kind: work?.kind ?? INTENT_KIND[f.type ?? ""] ?? o.kind,
        title: f.label,
        siteId: o.siteId,
        environmentId: o.environmentId,
        serverId: o.serverId,
        steps: [rp, next],
        status,
        startedAt: o.startedAt ?? o.createdAt,
        finishedAt: status === "running" ? null : (work?.finishedAt ?? o.finishedAt),
        logOp: work?.id ?? o.id,
        error: f.status === "cancelled" ? (f.error ?? o.error?.message ?? null) : work?.status === "failed" ? (work.error?.message ?? null) : null,
        outcome: work?.outcome ?? null,
        actor: o.actor,
        ...away(o),
      });
      continue;
    }
    if (!TASK_KINDS.has(o.kind)) continue;
    // A refresh the server ran by itself overnight isn't someone's task.
    if (o.kind === "server.refresh" && !o.actor) continue;
    const status: TaskStatus = isActiveOp(o.status) ? "running" : o.status === "succeeded" ? "succeeded" : o.status === "cancelled" ? "cancelled" : "failed";
    tasks.push({
      id: o.id,
      kind: o.kind,
      title: o.title,
      siteId: o.siteId,
      environmentId: o.environmentId,
      serverId: o.serverId,
      steps: [{ label: o.title, op: o, state: opState(o), estimateMs: est(o.kind, o) }],
      status,
      startedAt: o.startedAt ?? o.createdAt,
      finishedAt: o.finishedAt,
      logOp: o.id,
      error: o.status === "failed" || o.status === "expired" ? (o.error?.message ?? null) : null,
      outcome: o.outcome ?? null,
      actor: o.actor,
      ...away(o),
    });
  }
  const running = tasks.filter((t) => t.status === "running");
  const recent = tasks.filter((t) => t.status !== "running" && t.finishedAt && now - Date.parse(t.finishedAt) < RECENT_MS);
  return { running, recent };
}

/** The operations by id, and each chain's first step by the work it dispatched: for finding a bulk item's chain. */
export type OpIndex = { byId: Map<string, Operation>; before: Map<string, Operation> };
export function opIndex(ops: Operation[]): OpIndex {
  const before = new Map<string, Operation>();
  for (const o of ops) if (o.followUp?.operationId) before.set(o.followUp.operationId, o);
  return { byId: new Map(ops.map((o) => [o.id, o])), before };
}

/**
 * A bulk run item's steps, from the operations: its restore point and the update after it, or its one operation; null
 * before it has one. The item follows the restore point's operation first and the update's after, so either finds
 * the chain. Its own status settles them once it's final: the run's event can come before the operation's.
 * `typical`: each step's usual time, as for a task (the API's estimate on a running operation first), for its bar.
 */
export function itemSteps(operationId: string | null, status: string, idx: OpIndex, typical?: (serverId: string) => Typical | undefined): TaskStep[] | null {
  const op = operationId ? idx.byId.get(operationId) : undefined;
  if (!op) return null;
  const est = (kind: string, o?: Operation | null) => (o?.estimateSeconds ? o.estimateSeconds * 1000 : (typical?.(op.serverId)?.get(kind) ?? null));
  const rp = op.followUp ? op : (idx.before.get(op.id) ?? null);
  const steps: TaskStep[] = rp ? chainSteps(rp, rp === op ? (idx.byId.get(rp.followUp!.operationId ?? "") ?? null) : op, est) : [{ label: op.title, op, state: opState(op), estimateMs: est(op.kind, op) }];
  if (status === "succeeded") return steps.map((s) => ({ ...s, state: "done" }));
  if (status === "failed") return steps.map((s) => ({ ...s, state: s.state === "running" || s.state === "starting" ? "failed" : s.state === "waiting" ? "skipped" : s.state }));
  return steps;
}

/**
 * How far along a task is, 0..1, and whether the current step's share is known. Steps weigh what they usually take
 * when every step has a history, else the same. Within a running step: elapsed over usual, up to 90%; past 125% of
 * its usual time, or with no history, that step is indeterminate.
 */
export function progressOf(t: Pick<Task, "steps">, now = Date.now()): { done: number; current: number | null; slow: boolean; weights: number[] } {
  const all = t.steps.every((s) => s.estimateMs);
  const weights = t.steps.map((s) => (all ? s.estimateMs! : 1));
  const total = weights.reduce((a, b) => a + b, 0);
  let done = 0;
  let current: number | null = 0;
  let slow = false;
  t.steps.forEach((s, i) => {
    if (s.state === "done") done += weights[i]!;
    if (s.state === "running") {
      const began = s.op?.startedAt ?? s.op?.createdAt;
      const elapsed = began ? now - Date.parse(began) : 0;
      if (!s.estimateMs) current = null;
      else if (elapsed > s.estimateMs * 1.25) {
        current = null;
        slow = true;
      } else current = (Math.min(elapsed / s.estimateMs, 0.9) * weights[i]!) / total;
    }
  });
  return { done: done / total, current, slow, weights: weights.map((w) => w / total) };
}

/**
 * Typical durations for the servers with running tasks: each server's recent operations, read once in a while, in
 * the org the work is in (the person's work in another org reads that org's).
 */
export function useTypical(servers: { orgId: string; serverId: string }[]) {
  const qs = useQueries({
    queries: servers.map(({ orgId, serverId }) => ({
      queryKey: [...keys.org(orgId), "typical", serverId],
      queryFn: ({ signal }: { signal: AbortSignal }) => api.get<{ operations: Operation[] }>(`/orgs/${orgId}/operations?serverId=${serverId}&limit=100`, signal),
      staleTime: 10 * 60_000,
      select: (d: { operations: Operation[] }) => typicalFrom(d.operations),
    })),
  });
  const map = new Map<string, Typical>();
  servers.forEach(({ serverId }, i) => {
    const d = qs[i]?.data;
    if (d) map.set(serverId, d);
  });
  return (serverId: string) => map.get(serverId);
}
